import type { ElementType } from 'react';
import styled, { css, keyframes } from 'styled-components';

import {
  BasicParagraph,
  BiggerSubtitle,
  SmallTitle,
  style,
} from '@genially/design-system';
import { ArrowRightToBracketIcon } from '@genially/icons';

interface PlayerNameCommonProps {
  $isDanger: boolean;
  $hasSpaces: boolean;
}

export const PlayerNameLarge = styled(SmallTitle)<PlayerNameCommonProps>(
  ({ theme: { color, transition }, $isDanger, $hasSpaces }) => ({
    color: $isDanger ? color.content.error.default() : color.content.reversed.default(),
    transition: transition.standard('color'),

    ...style.mixins.ellipsis(true),

    whiteSpace: $hasSpaces ? 'normal' : 'break-spaces',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflowWrap: 'anywhere',
  }),
);

export const PlayerNameMedium = styled(BiggerSubtitle)<PlayerNameCommonProps>(
  ({ theme: { color, transition }, $isDanger, $hasSpaces }) => ({
    color: $isDanger ? color.content.error.default() : color.content.reversed.default(),
    transition: transition.standard('color'),

    ...style.mixins.ellipsis(true),

    whiteSpace: $hasSpaces ? 'normal' : 'break-spaces',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflowWrap: 'anywhere',
  }),
);

export const PlayerNameParagraph = styled(BasicParagraph)<PlayerNameCommonProps>(
  ({ theme: { color, transition }, $isDanger, $hasSpaces }) => ({
    color: $isDanger ? color.content.error.default() : color.content.reversed.default(),
    transition: transition.standard('color'),

    ...style.mixins.ellipsis(true),

    whiteSpace: $hasSpaces ? 'normal' : 'break-spaces',
    display: '-webkit-box',
    '-webkit-line-clamp': '2',
    '-webkit-box-orient': 'vertical',
    overflowWrap: 'anywhere',
  }),
);

const scaleInBounceKeyframes = keyframes({
  '0%': { transform: 'scale(1)' },
  '25%': { transform: 'scale(1.5)' },
  '50%': { transform: 'scale(1)' },
  '75%': { transform: 'scale(1.25)' },
  '100%': { transform: 'scale(1)' },
});

interface withScaleInBounceAnimationProps {
  $disableEnterAnimation?: boolean;
}

const withScaleInBounceAnimation = (Component: ElementType) =>
  styled(Component)<withScaleInBounceAnimationProps>(({ $disableEnterAnimation }) =>
    $disableEnterAnimation
      ? undefined
      : css`
          animation: ${scaleInBounceKeyframes} 1s ease;
        `,
  );

interface WrapperProps {
  $isSmall: boolean;
  onClick?: () => void;
}

export const Wrapper = withScaleInBounceAnimation(
  styled.div<WrapperProps>(({ theme: { color, size }, $isSmall, onClick }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: size.units(1.5),
    padding: $isSmall ? `${size.units(1)} ${size.units(3)}` : size.units(2),
    borderRadius: size.units(1),
    cursor: onClick ? 'pointer' : 'default',
    backgroundColor: color.background.tertiary.default(0.05),
    height: 'max-content',

    ...style.mixins.responsive.xs.downward({
      padding: `${size.units(1)} ${size.units(3)}`,
    }),

    [`${PlayerNameParagraph}`]: {
      flex: '1',
    },
  })),
);

export const IconWrapper = styled.div({
  width: '100%',
  height: '100%',
});

export const StyledArrowRightToBracketIcon = styled(ArrowRightToBracketIcon)(
  ({ theme: { color } }) => ({
    color: color.content.reversed.default(),
  }),
);

export const UserAliasAvatarWrapper = styled.div({
  position: 'relative',

  [IconWrapper]: {
    position: 'absolute',
    top: 0,
    right: 0,
  },
});
